import { useQuery } from '@tanstack/react-query';
import { customAxiosInstanceMutator } from './custom-axios-instance';

export interface NotificationHistoryItem {
  id: string;
  customerNumber: string;
  templateKey: string;
  method: 'EMAIL' | 'SMS' | 'WHATSAPP';
  status: 'PENDING' | 'SENT' | 'FAILED';
  recipient: string;
  subject?: string;
  content?: string;
  errorMessage?: string;
  sentAt?: string;
  createdAt: string;
  retryCount: number;
}

export interface NotificationHistoryPage {
  content: NotificationHistoryItem[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface GetNotificationHistoryParams {
  customerNumber?: string;
  templateKey?: string;
  status?: string;
  method?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}

export interface SendNotificationRequest {
  customerNumber: string;
  templateKey: string;
  method?: 'EMAIL' | 'SMS' | 'ALL';
  templateVariables?: Record<string, unknown>;
}

export interface NotificationTemplate {
  id: string;
  name: string;
  key: string;
  type: 'EMAIL' | 'SMS';
  subject?: string;
  content: string;
  active: boolean;
  description?: string;
}

export const getNotificationHistory = (
  params: GetNotificationHistoryParams,
  signal?: AbortSignal
) =>
  customAxiosInstanceMutator<NotificationHistoryPage>({
    url: `/notification/api/v1/history`,
    method: 'GET',
    params,
    signal,
  });

export const sendNotificationEmail = (
  body: SendNotificationRequest,
  signal?: AbortSignal
) =>
  customAxiosInstanceMutator<{ notificationId: string; status: string; message: string }>({
    url: `/notification/api/v1/email`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    signal,
  });

export const sendNotificationSms = (
  body: SendNotificationRequest,
  signal?: AbortSignal
) =>
  customAxiosInstanceMutator<{ notificationId: string; status: string; message: string }>({
    url: `/notification/api/v1/sms`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    signal,
  });

export const useGetNotificationHistory = (
  params: GetNotificationHistoryParams,
  options?: { enabled?: boolean }
) =>
  useQuery({
    queryKey: ['/notification/api/v1/history', params],
    queryFn: ({ signal }) => getNotificationHistory(params, signal),
    enabled: options?.enabled !== false,
  });

export const getNotificationTemplates = (signal?: AbortSignal) =>
  customAxiosInstanceMutator<NotificationTemplate[]>({
    url: `/notification/api/v1/templates`,
    method: 'GET',
    signal,
  });

// Templates rarely change, so cache them and reuse across rows to render the
// full email/SMS content behind a notification's templateKey.
export const useGetNotificationTemplates = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ['/notification/api/v1/templates'],
    queryFn: ({ signal }) => getNotificationTemplates(signal),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
